import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableRow from '@material-ui/core/TableRow'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Tooltip from '@material-ui/core/Tooltip'
import { fmt, fmtDate } from './formatters'
import { StyledTableHead } from '../ProjectsView.styles'
import {
  Section,
  SectionTitle,
  SectionSubtitle,
  OverviewTableContainer,
  HeaderCell,
  FirstHeaderCell,
  BodyCell,
  FirstBodyCell,
  RowLabel,
  Pill,
} from './ProjectOverview.styles'

const DeltaCell = ({ value, currency }) => {
  if (value === 0) return <BodyCell>$0</BodyCell>
  return (
    <BodyCell>
      <Pill data-testid="pill" data-positive={value > 0} positive={value > 0}>
        {fmt(value, currency)}
      </Pill>
    </BodyCell>
  )
}

const TOOLTIPS = {
  Staffed: 'Total submitted and approved hours × rate for this person in the selected month.',
  Pacing: 'Projected revenue based on remaining assignment days through month end.',
  'Over/Under-Pacing (against Staffed)':
    'Pacing minus Staffed. Positive = billing ahead of staffed amount. Negative = billing behind.',
}

const InfoTh = ({ label }) => (
  <HeaderCell>
    <RowLabel style={{ justifyContent: 'flex-end' }}>
      {label}
      <Tooltip title={TOOLTIPS[label]} placement="top">
        <InfoOutlinedIcon style={{ fontSize: 14, color: '#aaa' }} />
      </Tooltip>
    </RowLabel>
  </HeaderCell>
)

const PersonLevelTable = ({ persons, month, currency }) => {
  const subtitle = month ? moment(month, 'YYYY-MM').format('MMMM YYYY') : '—'

  return (
    <Section>
      <SectionTitle>Person Level Details</SectionTitle>
      <SectionSubtitle>{subtitle}</SectionSubtitle>

      <OverviewTableContainer>
        <Table size="small">
          <StyledTableHead>
            <TableRow>
              <FirstHeaderCell>Name</FirstHeaderCell>
              <HeaderCell style={{ textAlign: 'left' }}>Role</HeaderCell>
              <HeaderCell>Staff Start</HeaderCell>
              <HeaderCell>Staff End</HeaderCell>
              <HeaderCell>Allocation</HeaderCell>
              <HeaderCell>Rate</HeaderCell>
              <InfoTh label="Staffed" />
              <InfoTh label="Pacing" />
              <InfoTh label="Over/Under-Pacing (against Staffed)" />
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {persons.map(p => (
              <TableRow key={p.personId}>
                <FirstBodyCell>{p.name}</FirstBodyCell>
                <BodyCell style={{ textAlign: 'left' }}>{p.role}</BodyCell>
                <BodyCell>{fmtDate(p.staffStart)}</BodyCell>
                <BodyCell>{fmtDate(p.staffEnd)}</BodyCell>
                <BodyCell>{p.allocation}%</BodyCell>
                <BodyCell>{fmt(p.rate, currency)}</BodyCell>
                <BodyCell>{fmt(p.staffed, currency)}</BodyCell>
                <BodyCell>{fmt(p.pacing, currency)}</BodyCell>
                <DeltaCell value={p.pacing - p.staffed} currency={currency} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </OverviewTableContainer>
    </Section>
  )
}

PersonLevelTable.propTypes = {
  persons: PropTypes.arrayOf(
    PropTypes.shape({
      personId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      staffStart: PropTypes.string,
      staffEnd: PropTypes.string,
      allocation: PropTypes.number.isRequired,
      rate: PropTypes.number.isRequired,
      staffed: PropTypes.number.isRequired,
      pacing: PropTypes.number.isRequired,
    }),
  ).isRequired,
  month: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
}

export default PersonLevelTable
